import {
  Component,
  ElementRef,
  AfterViewInit,
  ViewChild,
  inject,
  Input,
  Output,
  EventEmitter,
  booleanAttribute,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { MockWidgets } from '../../../../shared/services/content/mock-data/mock-widgets.const';
import { WidgetContainerComponent } from '../../../widgets/widget-container/widget-container.component';
import {Position} from '../../../widgets/models/position';
import {GridCell} from '../../../widgets/models/grid-cell';
import {GridAddress} from '../../../widgets/models/grid-address';
import {Widget} from '../../../widgets/models/widget';

@Component({
  selector: 'via-grid',
  standalone: true,
  imports: [WidgetContainerComponent],
  templateUrl: './grid.component.html',
  styleUrl: './grid.component.scss',
})
export class GridComponent implements AfterViewInit, OnDestroy {
  // ============ Configuration Constants ============
  private readonly DEFAULT_CARD_WIDTH = 300;
  private readonly DEFAULT_CARD_HEIGHT = 200;
  private readonly MAX_RESOLVE_ITERATIONS = 10;

  // ============ Input/Output ============
  private _enabledWidgets: Widget[] = [];

  @Input()
  set enabledWidgets(value: Widget[] | null | undefined) {
    this._enabledWidgets = [...(value ?? [])];
    this.syncCardsFromEnabledWidgets();
  }

  get enabledWidgets(): Widget[] {
    return this._enabledWidgets;
  }

  @Output() saveWidgetStates = new EventEmitter<Widget[]>();
  @Output() widgetDisabled = new EventEmitter<string>();
  @Input() isLiveData = false;

  private _isEditMode = false;

  @Input({ transform: booleanAttribute })
  set isEditMode(value: boolean) {
    this._isEditMode = value;

    if (!this._isEditMode) {
      this.cancelActiveEditInteractions();
    }
  }

  get isEditMode(): boolean {
    return this._isEditMode;
  }

  // ============ Component Properties ============
  @ViewChild('gridContainer', { static: false })
  gridContainer!: ElementRef<HTMLElement>;

  private cdr = inject(ChangeDetectorRef);

  gridSize = 50;
  widgets: Widget[] = [];
  isGridOverlayVisible = false;
  gridOverlayCells: GridCell[] = [];
  private previewAreaCellKeys = new Set<string>();
  private previewOriginCellKey: string | null = null;

  // ============ Private State ============
  private containerWidth = 0;
  private containerHeight = 0;
  private resizeObserver?: ResizeObserver;

  // ============ Angular Lifecycle ============
  ngAfterViewInit(): void {
    this.updateContainerDimensions();
    this.generateOverlayCells();

    this.resizeObserver = new ResizeObserver(() =>
      this.onContainerResized(),
    );
    if (this.gridContainer?.nativeElement) {
      this.resizeObserver.observe(this.gridContainer.nativeElement);
    }

    setTimeout(() => {
      this.updateContainerDimensions();
      this.generateOverlayCells();
      this.validateNoOverlaps();
      this.cdr.detectChanges();
    }, 0);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
  }

  // ============ Public Event Handlers ============
  onWidgetClosed(cardId: string | number): void {
    if (!this.isEditMode) {
      return;
    }

    const widgetCount = this.widgets.length;
    this.widgets = this.widgets.filter((c) => c.widgetKey !== cardId);

    if (this.widgets.length === widgetCount) {
      return;
    }

    this.widgetDisabled.emit(String(cardId));
    this.saveState();
  }

  onWidgetPositionChanged(cardId: string, newAddress: GridAddress): void {
    if (!this.isEditMode) {
      return;
    }

    const card = this.widgets.find((c: Widget) => c.widgetKey === cardId);
    if (!card) return;

    if (this.gridAddressesEqual(card.gridAddress, newAddress)) {
      card.isDragging = false;
      return;
    }

    card.gridAddress = newAddress;
    card.isDragging = false;
    this.resolveAddressConflicts(card);
    this.validateNoOverlaps();
    this.saveState();
  }

  onWidgetDragStart(cardId: string): void {
    if (!this.isEditMode) {
      return;
    }

    const card = this.widgets.find((c: Widget) => c.widgetKey === cardId);
    if (card) {
      card.isDragging = true;
    }

    this.isGridOverlayVisible = true;
  }

  onWidgetDragEnd(widgetKey: string): void {
    const card = this.widgets.find((_widget) => _widget.widgetKey === widgetKey);
    if (card) {
      card.isDragging = false;
    }

    this.isGridOverlayVisible = this.widgets.some((_widget) => _widget.isDragging);
    if (!this.isGridOverlayVisible) {
      this.clearPreviewHighlight();
    }
  }

  onWidgetPreviewAddressChanged(address: GridAddress | null): void {
    if (!this.isEditMode) {
      this.clearPreviewHighlight();
      return;
    }

    if (!address) {
      this.clearPreviewHighlight();
      return;
    }

    const startCol = this.getColumnIndex(address.topLeft.column);
    const endCol = this.getColumnIndex(address.bottomRight.column);
    const startRow = address.topLeft.row;
    const endRow = address.bottomRight.row;

    this.previewAreaCellKeys.clear();
    for (let col = startCol; col <= endCol; col++) {
      for (let row = startRow; row <= endRow; row++) {
        this.previewAreaCellKeys.add(this.toCellKey(col, row));
      }
    }

    this.previewOriginCellKey = this.toCellKey(startCol, startRow);
  }

  isPreviewAreaCell(cell: GridCell): boolean {
    return this.previewAreaCellKeys.has(this.toCellKeyFromPixel(cell));
  }

  isPreviewOriginCell(cell: GridCell): boolean {
    if (!this.previewOriginCellKey) {
      return false;
    }

    return this.toCellKeyFromPixel(cell) === this.previewOriginCellKey;
  }

  onWidgetSnapped(cardId: string, position: Position): void {
    if (!this.isEditMode) {
      return;
    }

    const card = this.widgets.find((c: Widget) => c.widgetKey === cardId);
    if (!card) return;

    const finalPosition = this.constrainPositionToContainer(
      position,
      card,
    );

    const newAddress = this.calculateGridAddressFromPosition(
      finalPosition,
      card,
    );

    if (this.gridAddressesEqual(card.gridAddress, newAddress)) {
      card.isDragging = false;
      return;
    }

    card.gridAddress = newAddress;
    card.isDragging = false;
    this.resolveAddressConflicts(card);
    this.validateNoOverlaps();
    this.saveState();
  }

  // ============ Public Grid Methods ============
  getCardDimensions(address: GridAddress): {
    width: number;
    height: number;
  } {
    const width =
      (this.getColumnIndex(address.bottomRight.column) -
        this.getColumnIndex(address.topLeft.column) +
        1) *
      this.gridSize;
    const height =
      (address.bottomRight.row - address.topLeft.row + 1) * this.gridSize;
    return { width, height };
  }

  // ============ Private Grid Positioning Helpers ============
  private updateContainerDimensions(): void {
    if (this.gridContainer) {
      const rect = this.gridContainer.nativeElement.getBoundingClientRect();
      this.containerWidth = rect.width;
      this.containerHeight = rect.height;
    }
  }

  private onContainerResized(): void {
    this.updateContainerDimensions();
    this.generateOverlayCells();
  }

  private generateOverlayCells(): void {
    this.gridOverlayCells = [];

    if (this.containerWidth <= 0 || this.containerHeight <= 0) {
      return;
    }

    for (let x = 0; x < this.containerWidth; x += this.gridSize) {
      for (let y = 0; y < this.containerHeight; y += this.gridSize) {
        this.gridOverlayCells.push({ x, y, highlight: false, cardArea: false });
      }
    }
  }


  private cancelActiveEditInteractions(): void {
    this.widgets = this.widgets.map((card: Widget) => ({
      ...card,
      isDragging: false,
    }));
    this.isGridOverlayVisible = false;
    this.clearPreviewHighlight();
  }

  private clearPreviewHighlight(): void {
    this.previewAreaCellKeys.clear();
    this.previewOriginCellKey = null;
  }

  private toCellKey(col: number, row: number): string {
    return `${col}:${row}`;
  }

  private toCellKeyFromPixel(cell: GridCell): string {
    const col = Math.round(cell.x / this.gridSize);
    const row = Math.round(cell.y / this.gridSize) + 1;
    return this.toCellKey(col, row);
  }

  private getCardCellWidth(card: Widget): number {
    if (card.gridAddress) {
      return (
        this.getColumnIndex(card.gridAddress.bottomRight.column) -
        this.getColumnIndex(card.gridAddress.topLeft.column) +
        1
      );
    }
    return (
      card.defaultWidth ?? Math.ceil(this.DEFAULT_CARD_WIDTH / this.gridSize)
    );
  }

  private getCardCellHeight(card: Widget): number {
    if (card.gridAddress) {
      return (
        card.gridAddress.bottomRight.row - card.gridAddress.topLeft.row + 1
      );
    }
    return (
      card.defaultHeight ??
      Math.ceil(this.DEFAULT_CARD_HEIGHT / this.gridSize)
    );
  }

  private constrainPositionToContainer(
    position: Position,
    card: Widget,
  ): Position {
    const safePosition = {
      x: Math.max(0, position.x),
      y: Math.max(0, position.y),
    };

    const cellsWide = this.getCardCellWidth(card);
    const cellsHigh = this.getCardCellHeight(card);
    const cardPixelWidth = cellsWide * this.gridSize;
    const cardPixelHeight = cellsHigh * this.gridSize;

    const finalPosition = { ...safePosition };

    if (
      this.containerWidth > 0 &&
      finalPosition.x + cardPixelWidth > this.containerWidth
    ) {
      const maxValidX =
        Math.floor((this.containerWidth - cardPixelWidth) / this.gridSize) *
        this.gridSize;
      finalPosition.x = Math.max(0, maxValidX);
    }

    if (
      this.containerHeight > 0 &&
      finalPosition.y + cardPixelHeight > this.containerHeight
    ) {
      const maxValidY =
        Math.floor((this.containerHeight - cardPixelHeight) / this.gridSize) *
        this.gridSize;
      finalPosition.y = Math.max(0, maxValidY);
    }

    return finalPosition;
  }

  private calculateGridAddressFromPosition(
    position: Position,
    card: Widget,
  ): GridAddress {
    const cellsWide = this.getCardCellWidth(card);
    const cellsHigh = this.getCardCellHeight(card);
    const startCol = Math.floor(position.x / this.gridSize);
    const startRow = Math.floor(position.y / this.gridSize) + 1;

    return {
      topLeft: {
        column: this.getGridColumnLetter(Math.max(0, startCol)),
        row: Math.max(1, startRow),
      },
      bottomRight: {
        column: this.getGridColumnLetter(Math.max(0, startCol) + cellsWide - 1),
        row: Math.max(1, startRow) + cellsHigh - 1,
      },
    };
  }

  private positionToGridAddress(
    position: Position,
    cellsWide: number,
    cellsHigh: number,
  ): GridAddress {
    const startCol = Math.round(position.x / this.gridSize);
    const startRow = Math.round(position.y / this.gridSize) + 1;

    return {
      topLeft: { column: this.getGridColumnLetter(startCol), row: startRow },
      bottomRight: {
        column: this.getGridColumnLetter(startCol + cellsWide - 1),
        row: startRow + cellsHigh - 1,
      },
    };
  }

  private getGridColumnLetter(index: number): string {
    return String.fromCharCode(65 + Math.max(0, index));
  }

  private getColumnIndex(letter: string): number {
    return letter.charCodeAt(0) - 65;
  }

  private gridAddressesEqual(
    first?: GridAddress,
    second?: GridAddress,
  ): boolean {
    if (!first || !second) {
      return first === second;
    }

    return (
      first.topLeft.column === second.topLeft.column &&
      first.topLeft.row === second.topLeft.row &&
      first.bottomRight.column === second.bottomRight.column &&
      first.bottomRight.row === second.bottomRight.row
    );
  }

  // ============ Private Grid Positioning Finders ============
  private findSafePosition(
    cellsWide?: number,
    cellsHigh?: number,
  ): GridAddress {
    const defaultWidth =
      cellsWide ?? Math.ceil(this.DEFAULT_CARD_WIDTH / this.gridSize);
    const defaultHeight =
      cellsHigh ?? Math.ceil(this.DEFAULT_CARD_HEIGHT / this.gridSize);

    const maxCols = Math.floor(this.containerWidth / this.gridSize);
    const maxRows = Math.floor(this.containerHeight / this.gridSize);

    for (let row = 1; row <= Math.max(1, maxRows - defaultHeight + 1); row++) {
      for (let col = 0; col <= Math.max(0, maxCols - defaultWidth); col++) {
        const testAddress: GridAddress = {
          topLeft: { column: this.getGridColumnLetter(col), row: row },
          bottomRight: {
            column: this.getGridColumnLetter(col + defaultWidth - 1),
            row: row + defaultHeight - 1,
          },
        };

        if (
          !this.widgets.some((card) =>
            this.addressesOverlap(testAddress, card.gridAddress!),
          )
        ) {
          return testAddress;
        }
      }
    }

    return {
      topLeft: { column: 'A', row: 1 },
      bottomRight: {
        column: this.getGridColumnLetter(defaultWidth - 1),
        row: defaultHeight,
      },
    };
  }

  private findAlternativePosition(cardToMove: Widget, avoidAddress: GridAddress): GridAddress {
    if (!cardToMove.gridAddress) {
      return this.findSafePosition(
        cardToMove.defaultWidth,
        cardToMove.defaultHeight,
      );
    }

    const cardWidth = this.getCardCellWidth(cardToMove);
    const cardHeight = this.getCardCellHeight(cardToMove);
    const maxCols = Math.floor(this.containerWidth / this.gridSize);
    const maxRows = Math.floor(this.containerHeight / this.gridSize);

    for (let row = 1; row <= maxRows - cardHeight + 1; row++) {
      for (let col = 0; col <= maxCols - cardWidth; col++) {
        const testAddress: GridAddress = {
          topLeft: { column: this.getGridColumnLetter(col), row: row },
          bottomRight: {
            column: this.getGridColumnLetter(col + cardWidth - 1),
            row: row + cardHeight - 1,
          },
        };

        const hasConflict =
          this.widgets.some(
            (card) =>
              card.widgetKey !== cardToMove.widgetKey &&
              this.addressesOverlap(testAddress, card.gridAddress),
          ) || this.addressesOverlap(testAddress, avoidAddress);

        if (!hasConflict) {
          return testAddress;
        }
      }
    }

    return this.calculateNewAddress(avoidAddress, cardToMove.gridAddress);
  }

  // ============ Private Conflict Resolution ============
  private addressesOverlap(addr1: GridAddress, addr2?: GridAddress): boolean {
    if (!addr2) return false;

    const col1Start = this.getColumnIndex(addr1.topLeft.column);
    const col1End = this.getColumnIndex(addr1.bottomRight.column);
    const row1Start = addr1.topLeft.row;
    const row1End = addr1.bottomRight.row;

    const col2Start = this.getColumnIndex(addr2.topLeft.column);
    const col2End = this.getColumnIndex(addr2.bottomRight.column);
    const row2Start = addr2.topLeft.row;
    const row2End = addr2.bottomRight.row;

    return !(
      col1End < col2Start ||
      col2End < col1Start ||
      row1End < row2Start ||
      row2End < row1Start
    );
  }

  private calculateNewAddress(draggedAddress: GridAddress, otherAddress: GridAddress): GridAddress {
    const draggedWidth =
      this.getColumnIndex(draggedAddress.bottomRight.column) -
      this.getColumnIndex(draggedAddress.topLeft.column) +
      1;
    const draggedHeight =
      draggedAddress.bottomRight.row - draggedAddress.topLeft.row + 1;

    const otherWidth =
      this.getColumnIndex(otherAddress.bottomRight.column) -
      this.getColumnIndex(otherAddress.topLeft.column) +
      1;
    const otherHeight =
      otherAddress.bottomRight.row - otherAddress.topLeft.row + 1;

    const draggedCenterCol =
      this.getColumnIndex(draggedAddress.topLeft.column) + draggedWidth / 2;
    const draggedCenterRow = draggedAddress.topLeft.row + draggedHeight / 2;

    const otherCenterCol =
      this.getColumnIndex(otherAddress.topLeft.column) + otherWidth / 2;
    const otherCenterRow = otherAddress.topLeft.row + otherHeight / 2;

    const dx = draggedCenterCol - otherCenterCol;
    const dy = draggedCenterRow - otherCenterRow;

    let newTopLeftCol: number;
    let newTopLeftRow: number;

    if (Math.abs(dx) >= Math.abs(dy)) {
      // Move horizontally
      if (dx > 0) {
        newTopLeftCol =
          this.getColumnIndex(draggedAddress.topLeft.column) - otherWidth;
        newTopLeftRow = otherAddress.topLeft.row;
      } else {
        newTopLeftCol = this.getColumnIndex(draggedAddress.bottomRight.column) + 1;
        newTopLeftRow = otherAddress.topLeft.row;
      }
    } else {
      // Move vertically
      if (dy > 0) {
        newTopLeftCol = this.getColumnIndex(otherAddress.topLeft.column);
        newTopLeftRow = draggedAddress.topLeft.row - otherHeight;
      } else {
        newTopLeftCol = this.getColumnIndex(otherAddress.topLeft.column);
        newTopLeftRow = draggedAddress.bottomRight.row + 1;
      }
    }

    newTopLeftCol = Math.max(0, newTopLeftCol);
    newTopLeftRow = Math.max(1, newTopLeftRow);

    return {
      topLeft: {
        column: this.getGridColumnLetter(newTopLeftCol),
        row: newTopLeftRow,
      },
      bottomRight: {
        column: this.getGridColumnLetter(newTopLeftCol + otherWidth - 1),
        row: newTopLeftRow + otherHeight - 1,
      },
    };
  }

  private resolveAddressConflicts(sourceCard: Widget): void {
    if (!sourceCard || !sourceCard.gridAddress) return;

    const queue: Widget[] = [sourceCard];
    const processed = new Set<string>();

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (!current || !current.gridAddress) continue;
      processed.add(current.widgetKey);

      for (const other of this.widgets) {
        if (!other || other.widgetKey === current.widgetKey || !other.gridAddress) continue;

        if (this.addressesOverlap(current.gridAddress, other.gridAddress)) {
          const alternative = this.findAlternativePosition(
            other,
            current.gridAddress,
          );
          other.gridAddress = alternative;

          if (!processed.has(other.widgetKey)) {
            queue.push(other);
          }
        }
      }
    }
  }

  private validateNoOverlaps(): void {
    let changed = true;
    let iterations = 0;

    while (changed && iterations < this.MAX_RESOLVE_ITERATIONS) {
      changed = false;
      iterations++;

      for (let i = 0; i < this.widgets.length; i++) {
        const a = this.widgets[i];
        if (!a.gridAddress) continue;

        for (let j = i + 1; j < this.widgets.length; j++) {
          const b = this.widgets[j];
          if (!b.gridAddress) continue;

          if (this.addressesOverlap(a.gridAddress, b.gridAddress)) {
            const alternative = this.findAlternativePosition(b, a.gridAddress);
            if (alternative) {
              b.gridAddress = alternative;
              changed = true;
            }
          }
        }
      }
    }

    const remainingOverlaps: { a: string; b: string }[] = [];
    for (let i = 0; i < this.widgets.length; i++) {
      const a = this.widgets[i];
      if (!a.gridAddress) continue;

      for (let j = i + 1; j < this.widgets.length; j++) {
        const b = this.widgets[j];
        if (!b.gridAddress) continue;

        if (this.addressesOverlap(a.gridAddress, b.gridAddress)) {
          remainingOverlaps.push({ a: a.widgetKey, b: b.widgetKey });
        }
      }
    }

    if (remainingOverlaps.length > 0) {
      console.warn(
        'validateNoOverlaps: unresolved overlaps remain:',
        remainingOverlaps,
      );
    }
  }

  // ============ Private State Management ============
  private saveState(): void {
    const payload = this.widgets//.map((card) => ({
    //   widgetKey: card.widgetKey,
    //   displayName: card.title,
    //   enabled: true,
    //   defaultWidth: card.defaultWidth,
    //   defaultHeight: card.defaultHeight,
    //   gridAddress: card.gridAddress,
    //   isDragging: false,
    // }));
    this.saveWidgetStates.emit(payload);
  }

  private syncCardsFromEnabledWidgets(): void {
    let assignedMissingAddress = false;
    const nextCards = this.enabledWidgets.map((widget) => {
      const catalogCard = MockWidgets.find(
        (card) => card.widgetKey === widget.widgetKey || card.title === widget.title,
      );

      return {
        ...(catalogCard ?? {}),
        ...widget,
        defaultWidth: widget.defaultWidth ?? catalogCard?.defaultWidth,
        defaultHeight: widget.defaultHeight ?? catalogCard?.defaultWidth,
        gridAddress: widget.gridAddress,
        enabled: true,
        isDragging: false,
      } as Widget;
    });

    this.widgets = nextCards.map((card) => {
      if (card.gridAddress) {
        return card;
      }

      assignedMissingAddress = true;
      const cellsWide =
        card.defaultWidth ??
        Math.ceil(this.DEFAULT_CARD_WIDTH / this.gridSize);
      const cellsHigh =
        card.defaultHeight ??
        Math.ceil(this.DEFAULT_CARD_HEIGHT / this.gridSize);

      return {
        ...card,
        gridAddress: this.findSafePosition(cellsWide, cellsHigh),
      };
    });

    this.validateNoOverlaps();
    this.cdr.detectChanges();

    if (assignedMissingAddress) {
      this.saveState();
    }
  }
}
